import { useState } from "react";
import { Trans, useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import Text from "@CubeComponents/html/Text/Text";

interface ApplyScopeModalProps {
  /** Name of the layout being applied, for the prompt. */
  layoutName: string;
  onCancel: () => void;
  onConfirm: (args: { applyToAllProjects: boolean }) => void;
}

/**
 * Asks where an applied layout should take effect.
 *
 * The scope decides only WHERE THE SELECTION LANDS — "all projects" writes the caller's single
 * portal-wide selection row and clears their per-project overrides. The layout document itself is
 * stored once per portal and is never replicated, so this is not a copy operation.
 *
 * Owned by ProjectLayouts rather than the create modal, because it is reached from a row click
 * (applying an existing layout) as well as from create.
 */
function ApplyScopeModal({ layoutName, onCancel, onConfirm }: ApplyScopeModalProps) {
  const { t } = useTranslation();
  const [applyToAllProjects, setApplyToAllProjects] = useState(false);

  const modalContent = (
    <div className="my-2 flex flex-col gap-4">
      <Text className="text-sm font-normal">
        <Trans i18nKey="apply_layout_prompt" values={{ layoutName }} />
      </Text>
      <Text className="text-sm font-normal">
        {t("Choose where this layout should be applied. You can change it again at any time.")}
      </Text>
      <div className="flex flex-col gap-1">
        <RadioButton
          name="applyLayoutScope"
          label={t("Apply only for current project")}
          checked={!applyToAllProjects}
          onChange={() => setApplyToAllProjects(false)}
          data-testid="applyCurrentProjectRadio"
        />
        <RadioButton
          name="applyLayoutScope"
          label={t("Apply for all projects")}
          checked={applyToAllProjects}
          onChange={() => setApplyToAllProjects(true)}
          data-testid="applyAllProjectsRadio"
        />
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex gap-3">
      <ButtonComponent
        buttonType="gradientButton"
        onClick={() => onConfirm({ applyToAllProjects })}
        data-testid="applyScopeSubmit"
      >
        {t("Apply")}
      </ButtonComponent>
      <ButtonComponent buttonType="borderButton" onClick={onCancel} data-testid="applyScopeCancel">
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      headerTitle={t("Apply Layout")}
      modelContent={modalContent}
      footerContent={footerContent}
      onCancel={onCancel}
      width="48rem"
    />
  );
}

export default ApplyScopeModal;
